<template>
    <div class="pt-4">
        <div class="mb-4 flex items-center justify-between space-x-2">
            <h1 class="text-lg font-semibold text-gray-700">Specialties</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton @click="openCreate()" color="black" icon="i-ph-plus-bold">
                    Add specialty
                </UButton>
            </div>
        </div>

        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
            <UTable
                :rows="specialties"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-list-bullets-duotone', label: 'No specialties yet. Add the first one.' }"
                :ui="{ td: { padding: 'py-3' } }"
            >
                <template #name_en-data="{ row }">
                    <div class="flex items-center space-x-2">
                        <UIcon v-if="row.icon" :name="row.icon" class="size-5 text-gray-500" />
                        <span class="font-medium">{{ row.name_en }}</span>
                    </div>
                </template>
                <template #name_ar-data="{ row }">
                    <span dir="rtl" class="font-medium">{{ row.name_ar }}</span>
                </template>
                <template #active-data="{ row }">
                    <UToggle :model-value="row.active" @update:model-value="toggleActive(row, $event)" />
                </template>
                <template #actions-data="{ row, index }">
                    <UDropdown :items="actions(row, index)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <OverlayTelemedicineSpecialtyForm
            v-model:open="overlays.form"
            :specialty="selected"
            :next-order="nextOrder"
            @reload="fetchData"
        />
    </div>
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import { UserRole } from '~/types/user'
import type { Specialty } from '~/types/telemedicine'
import { countPublishedDoctors, specialtyInUseMessage } from '~/utils/telemedicineDoctor'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.telemedicineAdmin] })
useHead({ title: 'Telemedicine – Specialties' })

const client = useSupabaseClient()
const toast = useToast()

const loading = ref(false)
const search = ref('')
const specialties = ref<Specialty[]>([])
const selected = ref<Specialty>()

const overlays = reactive({
    form: false
})

const columns = [
    { key: 'sort_order', label: 'Order' },
    { key: 'name_en', label: 'English' },
    { key: 'name_ar', label: 'Arabic' },
    { key: 'active', label: 'Status' },
    { key: 'actions', class: 'w-px' },
]

const maxOrder = ref(0)
const moving = ref(false)
const nextOrder = computed(() => maxOrder.value + 1)

const actions = (specialty: Specialty, index: number) => [
    [
        {
            label: 'Edit',
            icon: 'i-ph-pencil-fill',
            click: () => {
                selected.value = specialty
                overlays.form = true
            },
        },
        {
            label: 'Move up',
            icon: 'i-ph-arrow-up-bold',
            disabled: index === 0 || !!search.value,
            click: () => move(index, -1),
        },
        {
            label: 'Move down',
            icon: 'i-ph-arrow-down-bold',
            disabled: index === specialties.value.length - 1 || !!search.value,
            click: () => move(index, 1),
        },
    ],
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })

async function fetchData() {
    loading.value = true

    let query = client.from('specialties')
        .select('*')
        .order('sort_order')
        .order('name_en')

    const term = search.value.replace(/[,()%*\\]/g, '').trim()

    if (term) {
        query = query.or(`name_en.ilike.%${term}%,name_ar.ilike.%${term}%`)
    }

    const [{ data, error }, { data: top, error: topError }] = await Promise.all([
        query,
        client.from('specialties').select('sort_order').order('sort_order', { ascending: false }).limit(1),
    ])

    if (error || topError) {
        toast.add({ title: 'Could not load specialties', description: (error ?? topError)!.message, color: 'red' })
    } else {
        specialties.value = (data ?? []) as unknown as Specialty[]
        maxOrder.value = (top as unknown as { sort_order: number }[] | null)?.[0]?.sort_order ?? 0
    }

    loading.value = false
}

function openCreate() {
    selected.value = undefined
    overlays.form = true
}

async function toggleActive(specialty: Specialty, active: boolean) {
    if (!active) {
        const { count, error: countError } = await countPublishedDoctors(client, specialty.id)
        if (countError) {
            toast.add({ title: 'Could not check doctors using this specialty', description: countError.message, color: 'red' })
            return
        }
        if (count) {
            toast.add({ title: specialtyInUseMessage(count), color: 'red' })
            return
        }
    }

    const { error } = await client.from('specialties')
        .update({ active } as never)
        .eq('id', specialty.id)

    if (error) {
        toast.add({ title: 'Could not update status', description: error.message, color: 'red' })
        return
    }

    specialty.active = active
    toast.add({ title: `${specialty.name_en} ${active ? 'activated' : 'deactivated'}` })
}

async function move(index: number, direction: -1 | 1) {
    const current = specialties.value[index]
    const neighbour = specialties.value[index + direction]

    if (!neighbour || moving.value) return

    moving.value = true

    try {
        const neighbourOrder = current.sort_order
        let currentOrder = neighbour.sort_order

        if (currentOrder === neighbourOrder) {
            currentOrder = current.sort_order + direction
        }

        const results = await Promise.all([
            client.from('specialties').update({ sort_order: currentOrder } as never).eq('id', current.id),
            client.from('specialties').update({ sort_order: neighbourOrder } as never).eq('id', neighbour.id),
        ])

        const failed = results.find((r) => r.error)

        if (failed?.error) {
            toast.add({ title: 'Could not reorder', description: failed.error.message, color: 'red' })
        }

        await fetchData()
    } finally {
        moving.value = false
    }
}
</script>
